/**
 * Shared bootstrap helpers: dedupe geo detection and plans catalog fetches
 * so Landing, Booking, useCurrency, and service pages do not repeat network work.
 */
import { detectUserCountry } from "./currency";
import { api } from "./api";

let countryPromise = null;
/** @type {Map<string, Promise<object>|object>} */
const plansByCountry = new Map();
/** @type {Map<string, Promise<object>|object>} */
const servicesByCountry = new Map();

/**
 * Single in-flight geo detection for the session.
 * @returns {Promise<import('./currency').Country & { serving?: boolean, region_available?: boolean }>}
 */
export function getDetectedCountry() {
  if (!countryPromise) {
    countryPromise = detectUserCountry();
  }
  return countryPromise;
}

/**
 * @param {string} countryCode
 * @returns {Promise<object>}
 */
export async function fetchPlansCatalog(countryCode) {
  const code = String(countryCode || "IN").toUpperCase();
  const cached = plansByCountry.get(code);
  if (cached) {
    return cached instanceof Promise ? cached : Promise.resolve(cached);
  }

  const request = api
    .get("/services/plans", { params: { country: code } })
    .then((response) => {
      plansByCountry.set(code, response.data);
      return response.data;
    })
    .catch((err) => {
      plansByCountry.delete(code);
      throw err;
    });

  plansByCountry.set(code, request);
  return request;
}

/**
 * @param {string} countryCode
 * @returns {Promise<object>}
 */
export async function fetchServicesPricing(countryCode) {
  const code = String(countryCode || "IN").toUpperCase();
  const cached = servicesByCountry.get(code);
  if (cached) {
    return cached instanceof Promise ? cached : Promise.resolve(cached);
  }

  const request = api
    .get("/services", { params: { country: code } })
    .then((response) => {
      servicesByCountry.set(code, response.data);
      return response.data;
    })
    .catch((err) => {
      servicesByCountry.delete(code);
      throw err;
    });

  servicesByCountry.set(code, request);
  return request;
}

/** Warm geo + plans catalog as early as possible (safe to call multiple times). */
export function prefetchCustomerPricing() {
  return getDetectedCountry()
    .then((country) => fetchPlansCatalog(country.code))
    .catch(() => null);
}
